import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ActionMenu,
  IconButton,
  IconMoreVert,
  TextLink,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import type { ReportActionState } from '@/features/invoice_reports/types';
import { useApplicationListActionMenu } from '../../hooks/useApplicationListActionMenu';
import styles from './ActionCell.module.scss';

const cx = classnames.bind(styles);

export interface ActionCellRecord {
  id: string;
  detail_path: string;
  edit_path: string;
  status: { key?: string };
  is_copyable: boolean;
  edit_action: ReportActionState;
  delete_action: ReportActionState;
  cancel_action: ReportActionState;
}

export type ActionCellProps = {
  record: ActionCellRecord;
  onDetail?: (reportId: string) => void;
  onDuplicate?: (reportId: string) => void;
  onDelete?: (reportId: string) => void;
  onCancel?: (reportId: string) => void;
};

/**
 * Action cell for 申請＞支払依頼一覧 (NA-4165).
 * Edit (visible/allowed) and 3-dot menu: 詳細, 複製, 削除, 取消し driven by edit_action, delete_action, cancel_action.
 * Tooltips from action reason when disabled.
 */
export const ActionCell: FC<ActionCellProps> = memo(
  ({ record, onDetail, onDuplicate, onDelete, onCancel }) => {
    const { t } = useTranslation();
    const dropDownProps = useMemo(() => ({ destroyPopupOnHide: true }), []);
    const container = useGlobalContainerRef();
    const {
      isEditVisible,
      isEditDisabled,
      editHref,
      menuItems,
      editDisabledTooltip,
      deleteMenuItemTooltip,
      cancelMenuItemTooltip,
    } = useApplicationListActionMenu({
      record: {
        id: record.id,
        detail_path: record.detail_path,
        edit_path: record.edit_path,
        status: record.status,
        is_copyable: record.is_copyable,
        edit_action: record.edit_action ?? {
          visible: false,
          allowed: false,
          reason: null,
        },
        delete_action: record.delete_action ?? {
          visible: false,
          allowed: false,
          reason: null,
        },
        cancel_action: record.cancel_action ?? {
          visible: false,
          allowed: false,
          reason: null,
        },
      },
      t,
      onDetail,
      onDuplicate,
      onDelete,
      onCancel,
    });

    const menuItemsWithStyleAndTooltips = useMemo(() => {
      const toTooltipTitle = (tooltip: string | undefined) =>
        tooltip ? t(tooltip) : undefined;
      return menuItems.map((item) => {
        const disabledLabelClass =
          item.key === 'cancel' && cancelMenuItemTooltip
            ? 'cancelLabelDisabled'
            : item.key === 'delete' && deleteMenuItemTooltip
            ? 'cancelLabelDisabled'
            : 'cancelLabel';
        const labelSpan = (
          <span className={cx(disabledLabelClass)}>{item.label}</span>
        );
        if (item.key === 'cancel' && cancelMenuItemTooltip) {
          return {
            ...item,
            label: (
              <Tooltip
                title={toTooltipTitle(cancelMenuItemTooltip)}
                overlayClassName={cx(styles['actionCellTooltip'])}
              >
                <div>{labelSpan}</div>
              </Tooltip>
            ),
          };
        }
        if (item.key === 'delete' && deleteMenuItemTooltip) {
          return {
            ...item,
            label: (
              <Tooltip
                title={toTooltipTitle(deleteMenuItemTooltip)}
                overlayClassName={cx(styles['actionCellTooltip'])}
              >
                <div>{labelSpan}</div>
              </Tooltip>
            ),
          };
        }
        if (item.key === 'delete' || item.key === 'cancel') {
          return { ...item, label: labelSpan };
        }
        return item;
      });
    }, [menuItems, cancelMenuItemTooltip, deleteMenuItemTooltip, t]);

    const editNode = useMemo(
      () =>
        !isEditVisible ? null : isEditDisabled ? (
          editDisabledTooltip ? (
            <Tooltip
              title={editDisabledTooltip ? t(editDisabledTooltip) : undefined}
              overlayClassName={cx(styles['actionCellTooltip'])}
              placement='leftBottom'
            >
              <span className={cx('editDisabled')}>{t('edit')}</span>
            </Tooltip>
          ) : (
            <span className={cx('editDisabled')}>{t('edit')}</span>
          )
        ) : (
          <TextLink href={editHref}>{t('edit')}</TextLink>
        ),
      [isEditVisible, isEditDisabled, editDisabledTooltip, t, editHref]
    );

    return (
      <div
        className={cx(styles['container'])}
        onClick={(e) => e.stopPropagation()}
      >
        {editNode}
        <ActionMenu
          type='custom'
          items={menuItemsWithStyleAndTooltips}
          dropDownProps={dropDownProps}
          getPopupContainer={container}
          className={cx('actionMenu')}
        >
          <IconButton size='xs' inVisible>
            <IconMoreVert size={20} />
          </IconButton>
        </ActionMenu>
      </div>
    );
  }
);

ActionCell.displayName = 'ActionCell';
